
import { ArrowLeft, Clock, User, Calendar, Tag, Users } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { blogPosts, authors, BlogPost } from '../data/blog-posts';
import { AdUnit } from '../components/AdUnit';
import { canonicalInternalHref } from '../lib/routes';
import { OptimizedImage } from '../components/ui/OptimizedImage';
import { InternalLink } from '../components/InternalLink';
import { formatPublishedDate } from '../lib/format-date';

const articleModules = import.meta.glob('../content/articles/*.md', {
  query: '?raw',
  import: 'default',
  eager: true
}) as Record<string, string>;

function getArticleContent(slug: string) {
  return articleModules[`../content/articles/${slug}.md`] || '';
}

interface BlogPostPageProps {
  slug: string;
  onNavigate: (path: string) => void;
}

export default function BlogPostPage({ slug, onNavigate }: BlogPostPageProps) {
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl mb-4">Post Not Found</h1>
          <InternalLink
            to="/blog"
            onNavigate={onNavigate}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer"
          >
            View All Posts
          </InternalLink>
        </div>
      </div>
    );
  }

  const content = getArticleContent(slug);

  return (
    <div className="min-h-screen bg-gray-50">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <InternalLink
          to="/blog"
          onNavigate={onNavigate}
          className="flex items-center gap-2 text-green-600 hover:text-green-800 hover:underline mb-8 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">Back to Articles</span>
        </InternalLink>

        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              {post.category}
            </span>
            {post.featured && (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                Featured
              </span>
            )}
          </div>

          <h1 className="text-4xl md:text-5xl mb-6">{post.title}</h1>

          <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <InternalLink to="/editorial-policy" onNavigate={onNavigate} className="hover:underline">By {post.author}</InternalLink>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{post.readTime} min read</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>Updated {formatPublishedDate(post.lastUpdated)}</span>
            </div>
          </div>

          {/* Images illustrate the topic; they are not evidence of an airport visit. */}
          <OptimizedImage
            src={post.imageUrl}
            alt=""
            className="w-full h-full object-cover"
            containerClassName="w-full h-96 rounded-lg shadow-lg"
            sizes="(max-width: 1023px) 100vw, 896px"
            loading="eager"
            fetchPriority="high"
          />
          <p className="text-sm text-gray-500 mt-2">Illustrative travel image.</p>
        </header>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-md p-5 sm:p-8 md:p-12 prose prose-green max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: () => null,
              h2: ({ node, ...props }: any) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b border-green-100 pb-2" {...props} />,
              h3: ({ node, ...props }: any) => <h3 className="text-xl font-bold text-gray-800 mt-6 mb-3" {...props} />,
              p: ({ node, ...props }: any) => <p className="text-gray-700 leading-relaxed mb-4 text-lg" {...props} />,
              ul: ({ node, ...props }: any) => <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700" {...props} />,
              ol: ({ node, ...props }: any) => <ol className="list-decimal list-inside space-y-2 mb-6 text-gray-700" {...props} />,
              li: ({ node, ...props }: any) => <li className="pl-2" {...props} />,
              a: ({ node, href, ...props }: any) => <a className="text-green-600 hover:text-green-800 hover:underline transition-colors" {...props} href={canonicalInternalHref(href || '')} />,
              blockquote: ({ node, ...props }: any) => <blockquote className="border-l-4 border-emerald-600 bg-emerald-50/80 p-5 md:p-6 my-8 rounded-r-xl shadow-xs text-gray-800 not-italic text-base md:text-lg leading-relaxed [&>p]:mb-0 [&>p]:text-gray-800" {...props} />,
              table: ({ node, ...props }: any) => <div className="overflow-x-auto my-8"><table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg" {...props} /></div>,
              th: ({ node, ...props }: any) => <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" {...props} />,
              td: ({ node, ...props }: any) => <td className="px-6 py-4 whitespace-normal text-sm text-gray-700 border-t border-gray-200" {...props} />,
              strong: ({ node, ...props }: any) => <strong className="font-semibold text-gray-900" {...props} />,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>

        {post.reviewedAt && (
          <section aria-labelledby="article-sources" className="mt-8 bg-white rounded-lg shadow-md p-6 md:p-8">
            <h2 id="article-sources" className="text-2xl font-bold mb-3">Sources and review</h2>
            <p className="text-gray-600 mb-4">
              Published {formatPublishedDate(post.publishedDate)}. Reviewed {formatPublishedDate(post.reviewedAt)} by the airports.ng Editorial Team.
              {' '}The references below support this guide. Confirm your own booking, eligibility and travel requirements with the relevant operator or authority before travel.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              {post.sources.map(source => (
                <li key={source.url}><a href={source.url} className="text-green-700 hover:underline break-words">{source.title}</a></li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              Found a change or an error? Email <a className="text-green-700 hover:underline" href={`mailto:hello@airports.ng?subject=${encodeURIComponent(`Correction: ${post.title}`)}`}>hello@airports.ng</a> with the page and supporting information.
            </p>
          </section>
        )}

        {post.reviewedAt && post.adsEligible && <AdUnit placement="article" />}

        {/* Tags */}
        <div className="mt-8 flex flex-wrap gap-2">
          {post.tags.map(tag => (
            <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center gap-1">
              <Tag className="w-3 h-3" />
              {tag}
            </span>
          ))}
        </div>

        {/* Our Team Section */}
        <div className="mt-12 p-8 bg-white rounded-lg shadow-md">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-6 h-6 text-green-600" />
            <h3 className="text-2xl">Our Team</h3>
          </div>
          <div className="text-gray-600 mb-6">
            <p>
              {authors.find(author => author.id === post.authorId)?.bio}
            </p>
          </div>
          <div className="text-center">
            <InternalLink
              to="/about#our-team"
              onNavigate={onNavigate}
              className="text-green-600 hover:text-green-700 hover:underline cursor-pointer"
            >
              Read about airports.ng →
            </InternalLink>
          </div>
        </div>

        {/* Related Posts */}
        <div className="mt-12">
          <h3 className="text-2xl mb-6">Related Articles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(() => {
              // Get related posts with improved algorithm
              const getRelatedPosts = (): BlogPost[] => {
                const otherPosts = blogPosts.filter(p => p.id !== post.id);

                if (otherPosts.length === 0) return [];

                // First priority: Same category
                const sameCategory = otherPosts.filter(p => p.category === post.category);
                if (sameCategory.length >= 2) {
                  return sameCategory.slice(0, 2);
                }

                // Second priority: Same category + posts with matching tags
                const matchingTags = otherPosts.filter(p =>
                  p.tags.some(tag => post.tags.includes(tag))
                );
                const combined = [...sameCategory, ...matchingTags.filter(p =>
                  !sameCategory.find(sp => sp.id === p.id)
                )];
                if (combined.length >= 2) {
                  return combined.slice(0, 2);
                }

                // Third priority: Same category + any other posts (sorted by recency)
                const allOther = [...sameCategory, ...otherPosts.filter(p =>
                  !sameCategory.find(sp => sp.id === p.id)
                )];
                return allOther
                  .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
                  .slice(0, 2);
              };

              const relatedPosts = getRelatedPosts();

              if (relatedPosts.length === 0) {
                return (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <p>No related articles available at this time.</p>
                  </div>
                );
              }

              return relatedPosts.map(relatedPost => (
                <InternalLink
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  onNavigate={onNavigate}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow text-left cursor-pointer"
                >
                  <OptimizedImage
                    src={relatedPost.imageUrl}
                    alt=""
                    className="w-full h-40 object-cover"
                    containerClassName="w-full h-40"
                  />
                  <div className="p-4">
                    <h4 className="mb-2 line-clamp-2">{relatedPost.title}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2">{relatedPost.excerpt}</p>
                  </div>
                </InternalLink>
              ));
            })()}
          </div>
        </div>
      </article>
    </div>
  );
}
